import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import type { CreateEmployeeRequest } from '@/api/model'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/shadcn/dialog'
import { Button } from '@/components/shadcn/button'
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/shadcn/table'

interface EmployeeImportPreviewDialogProps {
  open: boolean
  employees: CreateEmployeeRequest[]
  isUploading: boolean
  onConfirm: () => void
  onCancel: () => void
}

// Placeholder for empty preview cells (no group / sub-company / blank name).
const EMPTY_CELL = '—'

/**
 * Read-only confirmation dialog shown after parsing the import file: previews the
 * employees that would be uploaded (5-column summary of the 9-field payload) so the
 * user can confirm before the POST. Loading state locks the dialog while uploading.
 */
export function EmployeeImportPreviewDialog({
  open,
  employees,
  isUploading,
  onConfirm,
  onCancel,
}: EmployeeImportPreviewDialogProps) {
  const { t } = useTranslation(['employees', 'common'])

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !isUploading && onCancel()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('importPreview.title')}</DialogTitle>
          <DialogDescription>
            {t('importPreview.description', { count: employees.length })}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-auto rounded-md border">
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-background">
              <TableRow>
                <TableHead>{t('dialog.fields.firstName')}</TableHead>
                <TableHead>{t('dialog.fields.lastName')}</TableHead>
                <TableHead>{t('dialog.fields.employeeNumber')}</TableHead>
                <TableHead>{t('dialog.fields.groups')}</TableHead>
                <TableHead>{t('dialog.fields.subCompany')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {employees.map((emp, i) => (
                <TableRow key={`${emp.employeeNumber}-${i}`}>
                  <TableCell>{emp.firstName || EMPTY_CELL}</TableCell>
                  <TableCell>{emp.lastName || EMPTY_CELL}</TableCell>
                  <TableCell>{emp.employeeNumber || EMPTY_CELL}</TableCell>
                  <TableCell>
                    {emp.groupNames && emp.groupNames.length > 0
                      ? emp.groupNames.join(', ')
                      : EMPTY_CELL}
                  </TableCell>
                  <TableCell>{emp.subCompanyName || EMPTY_CELL}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={isUploading}>
            {t('buttons.cancel', { ns: 'common' })}
          </Button>
          <Button onClick={onConfirm} disabled={isUploading}>
            {isUploading && <Loader2 className="size-4 animate-spin" />}
            {t('importPreview.upload')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
